import { createContext, use, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useRepository } from '@/data/repository-context'
import type { User } from '@/data/types'

const SESSION_KEY = 'pass.session'
const ONBOARDED_KEY = 'pass.onboarded'

export type AuthStatus = 'checking' | 'authenticated' | 'anonymous'

export interface AuthContextValue {
  readonly status: AuthStatus
  readonly user: User | null
  /** False between signing up and finishing onboarding. */
  readonly onboarded: boolean
  login(credentials: { readonly email: string; readonly password: string }): Promise<void>
  /** Signing up authenticates you but leaves onboarding still to do. */
  signUp(credentials: { readonly email: string; readonly password: string }): Promise<void>
  completeOnboarding(): void
  logout(): void
}

const AuthContext = createContext<AuthContextValue | null>(null)

const readSession = (): boolean => {
  try {
    return window.localStorage.getItem(SESSION_KEY) === 'active'
  } catch {
    return false
  }
}

const writeSession = (active: boolean): void => {
  try {
    if (active) window.localStorage.setItem(SESSION_KEY, 'active')
    else window.localStorage.removeItem(SESSION_KEY)
  } catch {
    /* private mode — the session simply does not persist */
  }
}

const readOnboarded = (): boolean => {
  try {
    return window.localStorage.getItem(ONBOARDED_KEY) === 'done'
  } catch {
    return false
  }
}

const writeOnboarded = (done: boolean): void => {
  try {
    window.localStorage.setItem(ONBOARDED_KEY, done ? 'done' : 'pending')
  } catch {
    /* private mode — onboarding simply is not remembered */
  }
}

/**
 * Session state for the app. There is no auth backend yet, so a successful
 * login is a repository read plus a persisted flag — but every screen already
 * talks to this contract, so wiring a real token exchange is a change here only.
 */
export function AuthProvider({ children }: { readonly children: ReactNode }) {
  const repository = useRepository()
  // Trust the persisted flag synchronously so a signed-in person never sees a
  // loading flash on navigation; the profile is then filled in behind it.
  const [status, setStatus] = useState<AuthStatus>(() =>
    readSession() ? 'authenticated' : 'anonymous',
  )
  const [user, setUser] = useState<User | null>(null)
  const [onboarded, setOnboarded] = useState<boolean>(readOnboarded)

  useEffect(() => {
    if (status !== 'authenticated' || user) return
    let active = true
    repository
      .getUser()
      .then((next) => {
        if (active) setUser(next)
      })
      .catch(() => {
        if (!active) return
        writeSession(false)
        setUser(null)
        setStatus('anonymous')
      })
    return () => {
      active = false
    }
  }, [repository, status, user])

  const authenticate = useCallback(
    async (email: string, onboardedNext: boolean) => {
      const next = await repository.getUser()
      writeSession(true)
      writeOnboarded(onboardedNext)
      setOnboarded(onboardedNext)
      setUser({ ...next, email: email || next.email })
      setStatus('authenticated')
    },
    [repository],
  )

  // Onboarding belongs to registration only. Someone logging in already has a
  // set-up account, so a login always opens the app.
  const login = useCallback(
    ({ email }: { readonly email: string; readonly password: string }) =>
      authenticate(email, true),
    [authenticate],
  )

  const signUp = useCallback(
    ({ email }: { readonly email: string; readonly password: string }) =>
      authenticate(email, false),
    [authenticate],
  )

  const completeOnboarding = useCallback(() => {
    writeOnboarded(true)
    setOnboarded(true)
  }, [])

  const logout = useCallback(() => {
    writeSession(false)
    setUser(null)
    setStatus('anonymous')
  }, [])

  const value = useMemo<AuthContextValue>(
    () => ({ status, user, onboarded, login, signUp, completeOnboarding, logout }),
    [status, user, onboarded, login, signUp, completeOnboarding, logout],
  )

  return <AuthContext value={value}>{children}</AuthContext>
}

export function useAuth(): AuthContextValue {
  const context = use(AuthContext)
  if (!context) throw new Error('useAuth must be used inside an AuthProvider')
  return context
}
